import * as THREE from 'three';
import { MAZE } from '@somnhold/content';
import { hexDist, hexLen, hexToWorld, mazeKnowsAt, worldToHex, type BattleState, type Hex, type MapDef, type Maze } from '@somnhold/sim';
import { WORLD_PER_HEX } from './coords.ts';
import { glowMat } from '../materials.ts';
import { furrowsChest, type Chest } from '../models/chest.ts';

/*
 * The Furrows (docs/LABERINTOS.md), what the labyrinth puts on the crypt's floor:
 *
 * - The dark: every cell nobody in the group has mapped is under a black cap the colour of the void, so the halls
 *   show only as far as the group knows them (and a step around the hero). Caps sink away when a part is mapped.
 * - Levers by the gates: a stone post with an iron handle that drops when pulled, and a red light once it is.
 * - Camps: a low tent and a pole with a pennant, grey until pitched, then dragon red and lit.
 * - Ropes: a post and a coil of rope at the way in and at every pitched camp (where the party goes back up).
 * - Chests in the dead ends: a banded chest with a barrel lid and a keyhole glowing red; the lid stands open once taken.
 * - The waystone in the hall: a tall grey stone with a band of red runes.
 * - A ring on the floor under whatever is being worked, filling in pale as it is held.
 */

const S = WORLD_PER_HEX;
const K = S / 1000;
const at = (h: Hex): THREE.Vector3 => {
  const w = hexToWorld(h.q, h.r);
  return new THREE.Vector3(w.x * K, 0, w.y * K);
};
const RED = '#ff4f3a';
const RED_L = '#ff8a6e';
/** The dark over what nobody knows: unbroken rock, a shade under the walls' tops. */
const VOID = '#1b1d21';

const mat = (color: string, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial => new THREE.MeshStandardMaterial({ color, roughness: 0.8, flatShading: true, ...o });

export interface MazeLayerOpts {
  low: boolean;
}

interface Marker {
  group: THREE.Group;
  /** The part that moves or lights (lever handle, pennant, lid). */
  part: THREE.Object3D;
  glow?: THREE.PointLight;
}

export class MazeLayer {
  readonly group = new THREE.Group();
  private fog: THREE.InstancedMesh;
  private cells: Hex[] = [];
  /** 1 covered … 0 open, per fog cell (eased toward its target every frame). */
  private cover: Float32Array;
  private want: Float32Array;
  private fogSig = '';
  private levers: Marker[] = [];
  private camps: Marker[] = [];
  private chests: Chest[] = [];
  private ropes: THREE.Group[] = [];
  private rings: THREE.Mesh[] = [];
  private stone: THREE.PointLight | null = null;
  /** A ring on the floor at each camp not pitched yet (it pulses: stand here). */
  private campMarks: THREE.Mesh[] = [];
  /** A pale glow over each closed chest a hero is near, above the rock: seen from afar, a choice to turn aside. */
  private chestGlows: THREE.Sprite[] = [];
  /** A cold light on the hall's old ogre, so it is never a black shape. */
  private guardLight: THREE.PointLight;
  private pulse = 0;
  private dummy = new THREE.Object3D();

  constructor(
    map: MapDef,
    m: Maze,
    private opts: MazeLayerOpts,
  ) {
    this.group.name = 'maze';
    const L = m.layout;
    // The dark: a cap over every cell of the map (walls too, so the shape of the halls is not given away).
    const R = map.radius;
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) this.cells.push({ q, r });
    const cap = new THREE.CylinderGeometry(S / Math.sqrt(3) * 1.04, S / Math.sqrt(3) * 1.04, 2.3, 6);
    this.fog = new THREE.InstancedMesh(cap, new THREE.MeshBasicMaterial({ color: VOID }), this.cells.length);
    this.fog.name = 'maze-fog';
    this.fog.frustumCulled = false;
    this.cover = new Float32Array(this.cells.length).fill(1);
    this.want = new Float32Array(this.cells.length).fill(1);
    this.cells.forEach((_, i) => this.placeCap(i));
    this.fog.instanceMatrix.needsUpdate = true;
    this.group.add(this.fog);

    const stone = mat('#4a4c53');
    const stoneD = mat('#33353b');
    const wood = mat('#3a2a20');
    const iron = mat('#2d2e33', { metalness: 0.6, roughness: 0.4 });
    const rope = mat('#5d564e');
    const canvas = mat('#4b4f55', { side: THREE.DoubleSide });
    const pennant = mat('#6b6f78', { side: THREE.DoubleSide });
    const box = (w: number, h: number, d: number, m2: THREE.Material, parent: THREE.Object3D, x = 0, y = 0, z = 0): THREE.Mesh => {
      const o = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m2);
      o.position.set(x, y, z);
      parent.add(o);
      return o;
    };
    const cyl = (r0: number, r1: number, h: number, m2: THREE.Material, parent: THREE.Object3D, x = 0, y = 0, z = 0, seg = 7): THREE.Mesh => {
      const o = new THREE.Mesh(new THREE.CylinderGeometry(r0, r1, h, seg), m2);
      o.position.set(x, y, z);
      parent.add(o);
      return o;
    };
    const light = (parent: THREE.Object3D, y: number): THREE.PointLight | undefined => {
      if (this.opts.low) return undefined;
      const l = new THREE.PointLight(RED, 0, 3.2, 1.6);
      l.position.y = y;
      parent.add(l);
      return l;
    };
    const ring = (p: THREE.Vector3): THREE.Mesh => {
      const o = new THREE.Mesh(new THREE.RingGeometry(0.22 * S, 0.28 * S, 24, 1, 0, Math.PI * 2), new THREE.MeshBasicMaterial({ color: '#dfe4ff', transparent: true, opacity: 0.8, depthWrite: false }));
      o.rotation.x = -Math.PI / 2;
      o.position.copy(p).setY(0.03);
      o.visible = false;
      this.group.add(o);
      this.rings.push(o);
      return o;
    };

    // Levers, by the deeper side of each gate (off the crossing's middle, toward its gate).
    for (const lv of L.levers) {
      const n = at(L.nodes[lv.node] as Hex);
      const e = L.edges[lv.edge];
      const g = new THREE.Group();
      const toward = e ? at(e).sub(n).multiplyScalar(0.38) : new THREE.Vector3();
      g.position.copy(n).add(toward);
      cyl(0.09, 0.12, 0.5, stone, g, 0, 0.25, 0, 6);
      const arm = new THREE.Group();
      arm.position.y = 0.46;
      cyl(0.018, 0.018, 0.36, iron, arm, 0, 0.18, 0, 5);
      box(0.08, 0.05, 0.05, iron, arm, 0, 0.36, 0);
      arm.rotation.z = -0.7;
      g.add(arm);
      const glow = light(g, 0.7);
      this.group.add(g);
      this.levers.push({ group: g, part: arm, ...(glow ? { glow } : {}) });
      ring(g.position);
    }
    // Camps: a low tent and a pennant on a pole.
    for (const c of L.camps) {
      const g = new THREE.Group();
      g.position.copy(at(L.nodes[c] as Hex)).add(new THREE.Vector3(0.18 * S, 0, -0.12 * S));
      const tent = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.34, 4, 1, true), canvas);
      tent.position.y = 0.17;
      tent.rotation.y = Math.PI / 4;
      g.add(tent);
      cyl(0.015, 0.015, 0.9, wood, g, -0.3, 0.45, 0, 5);
      // Its own material: a pennant lights alone when its camp is pitched.
      const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.28, 0.16), pennant.clone());
      flag.position.set(-0.16, 0.8, 0);
      g.add(flag);
      const glow = light(g, 0.6);
      this.group.add(g);
      this.camps.push({ group: g, part: flag, ...(glow ? { glow } : {}) });
      ring(g.position);
    }
    // Chests in the dead ends, facing a corridor (models/chest.ts).
    for (const c of L.chests) {
      const k = furrowsChest();
      const g = k.model.root;
      g.position.copy(at(L.nodes[c] as Hex));
      g.rotation.y = (c % 6) * (Math.PI / 3);
      this.group.add(g);
      this.chests.push(k);
      ring(g.position);
    }
    for (const c of L.camps) {
      const mark = new THREE.Mesh(new THREE.RingGeometry(0.34 * S, 0.44 * S, 32), new THREE.MeshBasicMaterial({ color: RED_L, transparent: true, opacity: 0.7, depthWrite: false }));
      mark.rotation.x = -Math.PI / 2;
      mark.position.copy(at(L.nodes[c] as Hex)).setY(0.04);
      this.group.add(mark);
      this.campMarks.push(mark);
    }
    for (const c of L.chests) {
      const g = new THREE.Sprite(glowMat('#dfe4ff', 0.55, true));
      g.scale.setScalar(1.3);
      g.position.copy(at(L.nodes[c] as Hex)).setY(2.6);
      g.visible = false;
      this.group.add(g);
      this.chestGlows.push(g);
    }
    this.guardLight = new THREE.PointLight(0xcfd8ff, 0, 5, 1.2);
    this.group.add(this.guardLight);
    // The waystone in the hall.
    const ws = new THREE.Group();
    ws.position.copy(at(L.nodes[L.hall] as Hex));
    const pillar = cyl(0.16, 0.24, 1.4, stoneD, ws, 0, 0.7, 0, 6);
    pillar.rotation.y = 0.3;
    cyl(0.245, 0.245, 0.08, new THREE.MeshStandardMaterial({ color: RED_L, emissive: RED, emissiveIntensity: 1.4 }), ws, 0, 0.9, 0, 6);
    box(0.5, 0.12, 0.5, stone, ws, 0, 0.06, 0);
    this.group.add(ws);
    if (!this.opts.low) {
      this.stone = new THREE.PointLight(RED, 6, 5, 1.4);
      this.stone.position.set(0, 1.2, 0);
      ws.add(this.stone);
    }
    // Ropes: one per possible place (the way in and each camp); shown where there is one now.
    for (const n of [L.entry, ...L.camps]) {
      const g = new THREE.Group();
      g.position.copy(at(L.nodes[n] as Hex)).add(new THREE.Vector3(-0.22 * S, 0, 0.16 * S));
      cyl(0.03, 0.035, 1.1, wood, g, 0, 0.55, 0, 5);
      const coil = new THREE.Mesh(new THREE.TorusGeometry(0.12, 0.03, 5, 12), rope);
      coil.rotation.x = Math.PI / 2;
      coil.position.y = 0.05;
      g.add(coil);
      cyl(0.008, 0.008, 1.0, rope, g, 0.02, 1.3, 0, 4);
      this.group.add(g);
      this.ropes.push(g);
    }
  }

  private placeCap(i: number): void {
    const c = this.cells[i] as Hex;
    const v = this.cover[i] as number;
    const p = at(c);
    this.dummy.position.set(p.x, 1.15 * v - 0.05, p.z);
    this.dummy.scale.set(v > 0.01 ? 1 : 0, v > 0.01 ? Math.max(0.01, v) : 0, v > 0.01 ? 1 : 0);
    this.dummy.updateMatrix();
    this.fog.setMatrixAt(i, this.dummy.matrix);
  }

  /** What the group knows, and the state of everything it worked: once per sim tick. */
  sync(s: BattleState): void {
    const m = s.dungeon?.maze;
    if (!m) return;
    const L = m.layout;
    // What the group mapped and what the party's torches light: the same cells a hero can be sent to.
    const sig = `${m.seen.filter(Boolean).length}|${s.heroes.map((h) => (h.downTicks > 0 ? '-' : `${worldToHex(h.x, h.y).q},${worldToHex(h.x, h.y).r}`)).join(';')}`;
    if (sig !== this.fogSig) {
      this.fogSig = sig;
      this.cells.forEach((c, i) => {
        this.want[i] = mazeKnowsAt(s, m, c.q, c.r) ? 0 : 1;
      });
    }
    L.levers.forEach((_, i) => {
      const k = this.levers[i];
      if (!k) return;
      const on = !!m.gates[i];
      k.part.rotation.z += ((on ? 0.7 : -0.7) - k.part.rotation.z) * 0.3;
      if (k.glow) k.glow.intensity = on ? 3 : 0;
    });
    L.camps.forEach((_, i) => {
      const k = this.camps[i];
      if (!k) return;
      const on = !!m.camps[i];
      ((k.part as THREE.Mesh).material as THREE.MeshStandardMaterial).color.set(on ? RED : '#6b6f78');
      ((k.part as THREE.Mesh).material as THREE.MeshStandardMaterial).emissive.set(on ? RED : '#000000');
      ((k.part as THREE.Mesh).material as THREE.MeshStandardMaterial).emissiveIntensity = on ? 0.8 : 0;
      if (k.glow) k.glow.intensity = on ? 2.5 : 0;
    });
    L.chests.forEach((_, i) => {
      const k = this.chests[i];
      if (!k) return;
      k.lid.rotation.x += ((m.chests[i] ? -1.9 : 0) - k.lid.rotation.x) * 0.3;
      k.setOpen(!!m.chests[i]);
    });
    this.ropes.forEach((r, i) => {
      r.visible = i === 0 || !!m.camps[i - 1];
    });
    // Work rings: levers, then camps, then chests, in the order they were made.
    const holds = [...m.leverHold.map((v) => v / MAZE.leverTicks), ...m.campHold.map((v) => v / MAZE.campTicks), ...m.chestHold.map((v) => v / MAZE.chestTicks)];
    const done = [...m.gates, ...m.camps, ...m.chests];
    this.rings.forEach((r, i) => {
      const v = holds[i] ?? 0;
      r.visible = v > 0 && !done[i];
      r.scale.setScalar(0.4 + 0.6 * Math.min(1, v));
    });
    if (this.stone) this.stone.intensity = m.centreWon ? 3 : 12;
    L.camps.forEach((c, i) => {
      const mk = this.campMarks[i];
      if (mk) mk.visible = !m.camps[i] && !!m.seen[c];
    });
    const up = s.heroes.filter((h) => h.downTicks === 0).map((h) => worldToHex(h.x, h.y));
    L.chests.forEach((c, i) => {
      const g = this.chestGlows[i];
      if (g) g.visible = !m.chests[i] && up.some((h) => hexDist(h, L.nodes[c] as Hex) <= 8);
    });
    const ogre = m.guardian > 0 ? s.enemies.find((e) => e.id === m.guardian && e.hp > 0) : undefined;
    this.guardLight.intensity = ogre ? 9 : 0;
    if (ogre) this.guardLight.position.set(ogre.x * K, 2.2, ogre.y * K);
  }

  /** Every animation frame: the dark eases off what was just mapped. */
  frame(): void {
    this.pulse += 0.05;
    const k = 0.45 + 0.35 * Math.sin(this.pulse * 2);
    for (const mk of this.campMarks) (mk.material as THREE.MeshBasicMaterial).opacity = k;
    for (const g of this.chestGlows) g.material.opacity = 0.3 + 0.25 * Math.sin(this.pulse * 1.3);
    let any = false;
    for (let i = 0; i < this.cells.length; i++) {
      const w = this.want[i] as number;
      const v = this.cover[i] as number;
      if (v === w) continue;
      const nv = Math.abs(w - v) < 0.03 ? w : v + (w - v) * 0.18;
      this.cover[i] = nv;
      this.placeCap(i);
      any = true;
    }
    if (any) this.fog.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.group.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry.dispose();
        const mm = mesh.material as THREE.Material | THREE.Material[];
        if (Array.isArray(mm)) for (const x of mm) x.dispose();
        else mm.dispose();
      }
    });
    this.group.removeFromParent();
  }
}
